// src/pages/TeamDetailPage.js

import React, { useContext, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { ThemeContext } from '../context/ThemeContext';
import { userService } from '../api';
import PageLoader from '../components/common/PageLoader';
import ErrorDisplay from '../components/common/ErrorDisplay';
import ContactForm from '../components/forms/ContactForm';

// Styled components
const DetailSection = styled.section`
  padding: 5rem 2rem;
  background: ${props => props.theme.backgroundGradient || props.theme.background};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.5rem;
  margin-bottom: 2rem;
  background: ${props => props.theme.backgroundSecondary};
  color: ${props => props.theme.text};
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.primary};
    color: white;
    transform: translateX(-5px);
  }
  
  i {
    font-size: 0.9rem;
  }
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 3rem;
  margin-top: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const LeftColumn = styled.div``;

const RightColumn = styled.div``;

const ProfileCard = styled(motion.div)`
  background: ${props => props.theme.card.background};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: ${props => props.theme.card.shadow};
  margin-bottom: 2rem;
`;

const ProfileHeader = styled.div`
  position: relative;
`;

const CoverImage = styled.div`
  height: 150px;
  background: linear-gradient(45deg, ${props => props.theme.primary}, ${props => props.theme.primaryDark || props.theme.primary});
`;

const ProfileImageContainer = styled.div`
  position: relative;
  margin-top: -75px;
  padding: 0 2rem;
`;

const ProfileImage = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background-image: url(${props => props.src || '/default-avatar.jpg'});
  background-size: cover;
  background-position: center;
  border: 5px solid ${props => props.theme.card.background};
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
`;

const ProfileInfo = styled.div`
  padding: 2rem;
`;

const ProfileName = styled.h2`
  font-size: 2rem;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.text};
  font-weight: 700;
`;

const ProfileRole = styled.p`
  color: ${props => props.theme.primary};
  font-weight: 600;
  margin-bottom: 1.5rem;
  font-size: 1.2rem;
  letter-spacing: 0.5px;
`;

const InfoSection = styled.div`
  margin-bottom: 2rem;
`;

const SubSectionTitle = styled.h3`
  font-size: 1.3rem;
  color: ${props => props.theme.text};
  margin-bottom: 1rem;
  font-weight: 600;
  position: relative;
  padding-left: 1rem;
  
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 25%;
    height: 50%;
    width: 4px;
    background: ${props => props.theme.primary};
    border-radius: 4px;
  }
`;

const BioText = styled.p`
  color: ${props => props.theme.text};
  opacity: 0.9;
  line-height: 1.8;
  white-space: pre-line;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
`;

const InfoItem = styled.div`
  margin-bottom: 0.5rem;
`;

const InfoLabel = styled.p`
  font-size: 0.85rem;
  color: ${props => props.theme.text};
  opacity: 0.7;
  margin-bottom: 0.3rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.p`
  color: ${props => props.theme.text};
  font-weight: 500;
`;

const CardTitle = styled.h3`
  font-size: 1.3rem;
  color: white;
  margin-bottom: 0;
  padding: 1.2rem;
  background: ${props => props.theme.primary};
  font-weight: 600;
`;

const QRCodeContainer = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const QRCodeImage = styled.img`
  width: 200px;
  height: 200px;
  margin-bottom: 1rem;
  border: 1px solid ${props => props.theme.border};
`;

const QRCodeText = styled.p`
  color: ${props => props.theme.text};
  opacity: 0.8;
  font-size: 0.9rem;
  max-width: 250px;
  line-height: 1.6;
`;

const SocialLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 2rem;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  background: ${props => props.theme.backgroundSecondary};
  color: ${props => props.theme.text};
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  flex-grow: 1;
  
  i {
    font-size: 1.2rem;
  }
  
  &:hover {
    background: ${props => props.color || props.theme.primary};
    color: white;
    transform: translateY(-3px);
  }
`;

const ContactSection = styled.div`
  padding: 2rem;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 5rem 2rem;
  color: ${props => props.theme.text};
  opacity: 0.7;
  font-size: 1.2rem;
  background: ${props => props.theme.backgroundSecondary};
  border-radius: 12px;
  margin-top: 2rem;
`;

const SkillBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-top: 1rem;
`;

const SkillBadge = styled.span`
  display: inline-block;
  padding: 0.5rem 1rem;
  background: ${props => props.theme.backgroundSecondary};
  color: ${props => props.theme.primary};
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.primary};
    color: white;
    transform: translateY(-3px);
  }
`;

const TeamDetailPage = () => {
  const { memberSlug: id } = useParams();
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchMemberDetails = async () => {
      try {
        setLoading(true);
        
        // Récupérer les détails du membre
        const response = await userService.getById(id);
        
        console.log("Team member details:", response.data);
        setMember(response.data);
        
        // Mise à jour du titre de la page
        if (response.data && response.data.first_name) {
          document.title = `${response.data.first_name} ${response.data.last_name} | PropentaTech`;
        } else {
          document.title = `${t('teamMember')} | PropentaTech`;
        }
      } catch (err) {
        console.error("Error fetching member details:", err);
        setError(err.response?.data?.detail || t('errorLoadingMember'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchMemberDetails();
    
    return () => {
      document.title = 'PropentaTech';
    };
  }, [id, t]);
  
  // Format phone number for display
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    
    // Simple formatting, can be adjusted based on region
    const cleaned = phone.replace(/\D/g, '');
    
    if (cleaned.length === 10) {
      return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6, 10)}`;
    }
    
    return phone;
  };
  
  // Parse skills string into array
  const parseSkills = (skillsString) => {
    if (!skillsString) return [];
    
    return skillsString
      .split(',')
      .map(skill => skill.trim())
      .filter(skill => skill !== '');
  };
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (error) {
    return <ErrorDisplay error={error} />;
  }
  
  if (!member) {
    return (
      <DetailSection theme={theme}>
        <Container>
          <BackButton to="/team" theme={theme}>
            <i className="fas fa-arrow-left"></i> {t('backToTeam')}
          </BackButton>
          
          <NoResults theme={theme}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>
                <i className="fas fa-user-slash"></i>
              </div>
              <h3>{t('memberNotFound')}</h3>
              <p>{t('memberNotFoundDescription')}</p>
            </motion.div>
          </NoResults>
        </Container>
      </DetailSection>
    );
  }
  
  const skills = parseSkills(member.skills);
  
  return (
    <DetailSection theme={theme}>
      <Container>
        <BackButton to="/team" theme={theme}>
          <i className="fas fa-arrow-left"></i> {t('backToTeam')}
        </BackButton>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <ProfileGrid>
            <LeftColumn>
              <ProfileCard
                theme={theme}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <ProfileHeader>
                  <CoverImage theme={theme} />
                  <ProfileImageContainer>
                    <ProfileImage src={member.profile_picture} theme={theme} />
                  </ProfileImageContainer>
                </ProfileHeader>
                <ProfileInfo>
                  <ProfileName theme={theme}>
                    {member.first_name} {member.last_name}
                  </ProfileName>
                  <ProfileRole theme={theme}>
                    {member.position || t('teamMember')}
                  </ProfileRole>
                  
                  <InfoSection>
                    <SubSectionTitle theme={theme}>{t('contactInfo')}</SubSectionTitle>
                    <InfoGrid>
                      {member.email && (
                        <InfoItem>
                          <InfoLabel theme={theme}>{t('email')}</InfoLabel>
                          <InfoValue theme={theme}>{member.email}</InfoValue>
                        </InfoItem>
                      )}
                      
                      {member.phone_number && (
                        <InfoItem>
                          <InfoLabel theme={theme}>{t('phone')}</InfoLabel>
                          <InfoValue theme={theme}>{formatPhoneNumber(member.phone_number)}</InfoValue>
                        </InfoItem>
                      )}
                      
                      {member.department && (
                        <InfoItem>
                          <InfoLabel theme={theme}>{t('department')}</InfoLabel>
                          <InfoValue theme={theme}>{member.department}</InfoValue>
                        </InfoItem>
                      )}
                      
                      {member.gender && (
                        <InfoItem>
                          <InfoLabel theme={theme}>{t('gender')}</InfoLabel>
                          <InfoValue theme={theme}>{member.gender}</InfoValue>
                        </InfoItem>
                      )}
                    </InfoGrid>
                  </InfoSection>
                  
                  {skills.length > 0 && (
                    <InfoSection>
                      <SubSectionTitle theme={theme}>{t('skills')}</SubSectionTitle>
                      <SkillBadges>
                        {skills.map((skill, index) => (
                          <SkillBadge key={index} theme={theme}>
                            {skill}
                          </SkillBadge>
                        ))}
                      </SkillBadges>
                    </InfoSection>
                  )}
                </ProfileInfo>
              </ProfileCard>
              
              {member.qr_code && (
                <ProfileCard
                  theme={theme}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <CardTitle theme={theme}>{t('scanQRCode')}</CardTitle>
                  <QRCodeContainer>
                    <QRCodeImage src={member.qr_code} alt={`QR Code - ${member.first_name} ${member.last_name}`} theme={theme} />
                    <QRCodeText theme={theme}>
                      {t('scanQRCodeDescription')}
                    </QRCodeText>
                  </QRCodeContainer>
                </ProfileCard>
              )}
              
              <ProfileCard
                theme={theme}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <CardTitle theme={theme}>{t('connectWithMe')}</CardTitle>
                <SocialLinks>
                  {member.linkedin_profile && (
                    <SocialLink
                      href={member.linkedin_profile}
                      target="_blank"
                      rel="noopener noreferrer"
                      theme={theme}
                      color="#0077B5"
                    >
                      <i className="fab fa-linkedin-in"></i>
                        {t('LinkedIn')}
                        </SocialLink>
                  )}
                  
                  {member.github_profile && (
                    <SocialLink
                      href={member.github_profile}
                      target="_blank"
                      rel="noopener noreferrer"
                      theme={theme}
                      color="#333333"
                    >
                      <i className="fab fa-github"></i>
                      GitHub
                    </SocialLink>
                  )}
                  
                  {member.twitter_profile && (
                    <SocialLink
                      href={member.twitter_profile}
                      target="_blank"
                      rel="noopener noreferrer"
                      theme={theme}
                      color="#1DA1F2"
                    >
                      <i className="fab fa-twitter"></i>
                      Twitter
                    </SocialLink>
                  )}
                  
                  {member.facebook_profile && (
                    <SocialLink
                      href={member.facebook_profile}
                      target="_blank"
                      rel="noopener noreferrer"
                      theme={theme}
                      color="#4267B2"
                    >
                      <i className="fab fa-facebook-f"></i>
                      Facebook
                    </SocialLink>
                  )}
                </SocialLinks>
              </ProfileCard>
            </LeftColumn>
            
            <RightColumn>
              <ProfileCard
                theme={theme}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <CardTitle theme={theme}>{t('about')}</CardTitle>
                <InfoSection style={{ padding: '2rem' }}>
                  {member.bio ? (
                    <BioText theme={theme}>{member.bio}</BioText>
                  ) : (
                    <BioText theme={theme}>{t('noBioAvailable')}</BioText>
                  )}
                </InfoSection>
              </ProfileCard>
              
              <ProfileCard
                theme={theme}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <CardTitle theme={theme}>{t('contactMember')}</CardTitle>
                <ContactSection>
                  <p style={{ marginBottom: '1.5rem', color: theme.text, opacity: 0.8 }}>
                    {t('contactMemberDescription')}
                  </p>
                  <ContactForm recipient={member} />
                </ContactSection>
              </ProfileCard>
              
              {/* Additional Information Card */}
              <ProfileCard
                theme={theme}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <CardTitle theme={theme}>{t('additionalInfo')}</CardTitle>
                <InfoSection style={{ padding: '2rem' }}>
                  <InfoGrid>
                    {member.age && (
                      <InfoItem>
                        <InfoLabel theme={theme}>{t('age')}</InfoLabel>
                        <InfoValue theme={theme}>{member.age}</InfoValue>
                      </InfoItem>
                    )}
                    
                    {member.address && (
                      <InfoItem>
                        <InfoLabel theme={theme}>{t('location')}</InfoLabel>
                        <InfoValue theme={theme}>{member.address}</InfoValue>
                      </InfoItem>
                    )}
                    
                    {member.date_joined && (
                      <InfoItem>
                        <InfoLabel theme={theme}>{t('joinedTeam')}</InfoLabel>
                        <InfoValue theme={theme}>
                          {new Date(member.date_joined).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </InfoValue>
                      </InfoItem>
                    )}
                  </InfoGrid>
                  
                  {/* Staff Badge */}
                  {member.is_staff && (
                    <div style={{ 
                      display: 'inline-block', 
                      marginTop: '2rem',
                      padding: '0.5rem 1.5rem',
                      background: theme.primary,
                      color: 'white',
                      borderRadius: '30px',
                      fontWeight: '600',
                      fontSize: '0.9rem'
                    }}>
                      <i className="fas fa-certificate" style={{ marginRight: '0.5rem' }}></i>
                      {t('officialTeamMember')}
                    </div>
                  )}
                </InfoSection>
              </ProfileCard>
            </RightColumn>
          </ProfileGrid>
        </motion.div>
      </Container>
    </DetailSection>
  );
};

export default TeamDetailPage;